// src/pages/Reports/BillWiseProfitLoss/BillWiseProfitLossReport.jsx
import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import {
  Card,
  Row,
  Col,
  Button,
  Typography,
  Table,
  message,
  Select,
  Grid,
  Flex,
} from "antd";
import {
  ArrowLeftOutlined,
  DownloadOutlined,
  PrinterOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import ReactToPrint from "react-to-print";
import dayjs from "dayjs";
import _ from "lodash";

import { setFilters } from "./billWiseProfitLossSlice";
import { fetchBillWiseProfitLoss } from "./billWiseProfitLossThunk";
import RecordDateFilter from "../../../components/filters/RecordDateFilter";
import SearchListAutoComplete from "../../../components/SearchListAutoComplete";
import ReportDom from "../../../components/common/ReportDom";

import "./BillWiseProfitLossReport.css";
import { fetchCustomers } from "../../../store/commonThunks/lookups";
import BillWiseProfitLossTable from "./component/BillWiseProfitLossTable";
import { useDefaultDateRange } from "../../../hooks/useDefaultDateRange";
import CommonDateRangeFilter from "../../../components/common/filters/CommonDateRangeFilter";
import { exportBillWiseProfitLossCsv } from "./component/exportBillWiseProfitLossCsv";

const { Title } = Typography;

const BillWiseProfitLossReport = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const reportRef = useRef();
  const { useBreakpoint } = Grid;
  const screens = useBreakpoint();

  const { filters, data, productSummary, totals, othersReport, loading } =
    useSelector((state) => state.billWiseProfitLoss);
  const { customers } = useSelector((s) => s.lookups);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
  });

  const handleTableChange = (pagination) => {
    setPagination(pagination);
  };

  useEffect(() => {
    dispatch(fetchCustomers());
  }, []);

    // ✅ Auto set default date to today
    useDefaultDateRange(setFilters, filters, "today");

  const handleFilterChange = (field, value) => {
    dispatch(setFilters({ [field]: value }));
  };

  const handleReport = () => {
    if (!filters.fromDate || !filters.toDate) {
      message.warning("Please select date range");
      return;
    }
    dispatch(fetchBillWiseProfitLoss(filters));
  };

  const handlePrint = useReactToPrint({
    contentRef: reportRef,
    documentTitle: "Bill Wise Profit & Loss Report",
    removeAfterPrint: true,
  });

  const handleNavigateToBack = () => {
    navigate("/reports");
  };

  const columns = [
    { title: "Code", dataIndex: "item_code", key: "item_code" },
    { title: "Product Name", dataIndex: "item_name", key: "item_name" },
    {
      title: "Sold Rate",
      dataIndex: "sale_rate",
      align: "right",
      render: (val) => (val != null ? parseFloat(val).toFixed(2) : "0.00"),
    },
    { title: "Sold Quantity", dataIndex: "sale_qty", align: "right" },
   {
  title: "Sold",
  dataIndex: "netSoldAmount",
  render: (val) => (val != null ? parseFloat(val).toFixed(2) : "0.00")
},

    {
      title: "Purchase Rate",
      dataIndex: "purchase_average_rate",
      align: "right",
      render: (val) => (val != null ? parseFloat(val).toFixed(2) : "0.00"),
    },
    {
      title: "Purchased",
      dataIndex: "purchasedAmount",
      align: "right",
      render: (val) => (val != null ? parseFloat(val).toFixed(2) : "0.00"),
    },
    {
      title: "Profit/Loss",
      dataIndex: "productProfitLoss",
      align: "right",
      render: (val) => (val != null ? parseFloat(val).toFixed(2) : "0.00"),
    },
  ];
  const handleDownloadCsv = () => {
    exportBillWiseProfitLossCsv({
      data,
      productSummary,
      totals,
      filters,
      title:    "Bill Wise Profit & Loss Report",
      filename: "bill_wise_profit_loss_report",
    });
  };

  // ← added — true when either mode has rows
  const hasData =
    filters.filterType === "product_summary"
      ? productSummary.length > 0
      : data.length > 0;
  return (
    <div className="p-4">
      {/* Header Card */}
      <Card
        className="shadow-md rounded-xl mb-4"
        title={
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="text"
              icon={<ArrowLeftOutlined />}
              onClick={handleNavigateToBack}
              style={{ marginRight: 8 }}
            />
            <Title level={4} style={{ margin: 0 }} className="reportBigTitle" >
              Bill Wise Profit & Loss Report
            </Title>
          </div>
        }
        extra={
          // ← replaced single Button with Flex + two buttons
          <Flex gap={8}>
            {hasData && (
              <Button icon={<DownloadOutlined />} onClick={handleDownloadCsv}>
                {!screens.xs && "CSV"}
              </Button>
            )}
            <Button type="primary" icon={<PrinterOutlined />} onClick={handlePrint}>
              {!screens.xs && "Print"}
            </Button>
          </Flex>
        }
      />

      {/* Filters */}
      {/* <Card className="shadow-md rounded-xl mb-4"> */}
      <Card className="product-replace-filters mb-4 shadow-md rounded-xl">
        <Row gutter={[16, 16]}>
          {/* Record Type Dropdown */}
          <Col xs={24} sm={12} md={6} lg={4}>
            <label>Record Type</label>
            <Select
              value={filters.filterType || "all"}
              onChange={(val) => handleFilterChange("filterType", val)}
              style={{ width: "100%" }}
              options={[
                { value: "all", label: "All" },
                { value: "customer", label: "Customer Wise" },
              ]}
            />
          </Col>

          {/* Customer Dropdown (only when filterType = customer) */}
          {filters.filterType === "customer" && (
            <Col xs={24} sm={12} md={6} lg={6}>
              <label>Select Customer</label>
              <SearchListAutoComplete
                entityType="customer"
                style={{ width: "100%" }}
                optionsList={customers.map((c) => ({
                  sale_id: c.acc_id,
                  display_text: c.acc_name,
                }))}
                onSelect={(obj) =>
                  handleFilterChange("customerId", obj?.sale_id)
                }
                placeholder="Customer"
                showAddNew={false}
              />
            </Col>
          )}

          {/* Date Range Filter */}
          {/* <RecordDateFilter
            filters={filters}
            handleFilterChange={handleFilterChange}
          /> */}
          <CommonDateRangeFilter
            filters={filters}
            onChange={(fromDate, toDate) => {
            handleFilterChange("fromDate", fromDate);
            handleFilterChange("toDate", toDate);
            }}
          />

          {/* Report Button */}
          <Col
            xs={24}
            sm={12}
            md={6}
            lg={4}
            style={{ display: "flex", alignItems: "flex-end", height: 58  }}
          >
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleReport}
              loading={loading}
              block
              style={{height: 35.18}}
            >
              Report
            </Button>
          </Col>
        </Row>
        <style>{`
    @media (min-width: 768px) and (max-width: 1024px) {
      .product-replace-filters .ant-row .ant-col {
        flex: 0 0 50% !important;
        max-width: 50% !important;
      }
    }
      @media (min-width: 768px) {
  :where(.css-dev-only-do-not-override-1j5i8gz).ant-col-md-4 {
    max-width: none !important;
    flex: none !important;
  }
}

  `}</style>
      </Card>

      {/* Report */}
      <Card className="shadow-md rounded-xl">
        {filters.filterType !== "product_summary" ? (
          <>
            {data.map((sale, idx) => {
       const subSold = _.sumBy(sale.details, d => Number(d.netSoldAmount || 0));
       const subPurchased = _.sumBy(sale.details, d => Number(d.purchasedAmount || 0));
       const subProfit = _.sumBy(sale.details, d => Number(d.productProfitLoss || 0));

              return (
                <div key={idx} className="mb-6">
                  {/* Invoice header */}
                  <div className="font-semibold mb-2">
                    Sales Invoice No: {sale.sale_invoice} | Sales Date & Time:{" "}
                    {dayjs(sale.sale_created_isodt).format(
                      "DD MMM YYYY hh:mm A"
                    )}{" "}
                    | Customer: {sale.customer_name} ({sale.customer_code})
                  </div>

                  {/* Invoice Table */}
                  <Table
                    columns={columns}
                    dataSource={sale.details}
                    rowKey="item_code"
                    pagination={{
                      ...pagination,
                      showSizeChanger: true,
                      pageSizeOptions: ["10", "20", "50"],
                    }}
                    onChange={handleTableChange}
                    bordered
                    size="small"
                    scroll={{ x: "max-content" }}
                    rowClassName={(_, index) =>
                      index % 2 === 0 ? "table-row-light" : "table-row-dark"
                    }
                    summary={() => (
                      <Table.Summary.Row
                        style={{ background: "#e6f4ff", fontWeight: "bold" }}
                      >
                        <Table.Summary.Cell colSpan={4} align="right">
                          Sub Total:
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          {subSold.toFixed(2)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell />
                        <Table.Summary.Cell align="right">
                          {subPurchased.toFixed(2)}
                        </Table.Summary.Cell>
                        <Table.Summary.Cell align="right">
                          {subProfit.toFixed(2)}
                        </Table.Summary.Cell>
                      </Table.Summary.Row>
                    )}
                  />
                </div>
              );
            })}

            {/* Grand Totals */}
            {data.length > 0 && (
              <div
                style={{
                  marginTop: "12px",
                  padding: "10px 16px",
                  background: "#5f726d",
                  color: "white",
                  fontWeight: "bold",
                  display: "flex",
                  flexWrap: "wrap", // ✅ allow wrapping on small screens
                  gap: "12px", // ✅ spacing between items
                  justifyContent: "space-between",
                  borderRadius: "4px",
                }}
              >
                <span style={{ whiteSpace: "nowrap" }}>
                  Total Sold: {totals.totalSold.toFixed(2)}
                </span>
                <span style={{ whiteSpace: "nowrap" }}>
                  Total Purchased: {totals.totalPurchased.toFixed(2)}
                </span>
                <span style={{ whiteSpace: "nowrap" }}>
                  Total Profit/Loss: {totals.totalProfit.toFixed(2)}
                </span>
                {/* <span style={{ whiteSpace: "nowrap" }}>
                  Discount: {totals.totalDiscount.toFixed(2)}
                </span> */}
              </div>
            )}
          </>
        ) : (
          <Table
            dataSource={productSummary}
            rowKey="prod_code"
            pagination={false}
            size="small"
            scroll={{ x: "max-content" }}
            columns={[
              { title: "Product Name", dataIndex: "prod_name" },
              { title: "Product Code", dataIndex: "prod_code" },
              { title: "Sold Qty", dataIndex: "prod_sold_qty", align: "right" },
              {
                title: "Sold Amount",
                dataIndex: "prod_sold_amount",
                align: "right",
              },
              {
                title: "Purchased Amount",
                dataIndex: "prod_purchased_amount",
                align: "right",
              },
              {
                title: "Profit Amount",
                dataIndex: "prod_profit_amount",
                align: "right",
              },
            ]}
            summary={() => {
              const totalSold = _.sumBy(productSummary, "prod_sold_amount");
              const totalPurchased = _.sumBy(
                productSummary,
                "prod_purchased_amount"
              );
              const totalProfit = _.sumBy(productSummary, "prod_profit_amount");
              return (
                <Table.Summary.Row>
                  <Table.Summary.Cell colSpan={3} align="right">
                    Total:
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    {totalSold.toFixed(2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    {totalPurchased.toFixed(2)}
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right">
                    {totalProfit.toFixed(2)}
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        )}
      </Card>

      {/* Print Layout */}
      {/* Print Layout */}
      {/* Hidden print layout */}
      <div style={{ position: "absolute", top: "-9999px", left: "-9999px" }}>
        <ReportDom
          ref={reportRef}
          title="Bill Wise Profit & Loss Report"
          mode="report-page"
          filters={filters}
          renderTable={() => (
            <BillWiseProfitLossTable
              data={data}
              productSummary={productSummary}
              totals={totals}
              filters={filters}
              printMode={true}
            />
          )}
        />
      </div>
    </div>
  );
};

export default BillWiseProfitLossReport;
